import React, { useState, useEffect, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { renderIcon } from '../../utils/renderIcon';
import { useAuth } from '../../../../AuthProvider';
import { useDocSheetOpen } from '../../../../../utils/docSheetPresence';
import { isAttachmentPreviewOpen } from '../../../../../utils/attachmentPreviewPresence';
import { isVariablesPanelOpen } from '../../../../../utils/variablesPanelPresence';
import { useAppMainOverlayHost } from '../../../../../context/AppMainOverlayContext';

// Section components
import AttachedDocsSection from './components/AttachedDocsSection';
import MetadataBar from './components/MetadataBar';
import MetadataSection from './components/MetadataSection';
import CustomFieldsSidebarSection from './components/CustomFieldsSidebarSection';
import ConfirmModal from '../../components/ConfirmModal';
import { DocSheetCloseButton } from '../../../../DocsArea/DocSheetCloseButton';

// CSS imports
import './styles/layout.css';
import './styles/header.css';
import './styles/sections.css';
import './styles/content.css';
import './styles/metadata/index.css';

/**
 * ============================================================================
 * MISSION FULL VIEW (internally: TaskDetailSidebar)
 * ============================================================================
 * 
 * TERMINOLOGY GUIDE FOR AI/DEVELOPERS:
 * ------------------------------------
 * - "Campaign" = Sprint (a collection of missions/tasks)
 * - "Mission" = Task (an individual work item)
 * - "Mission Full View" = This component - the comprehensive modal for viewing
 *   and editing a single mission, similar to Notion's page view or ClickUp's
 *   task detail view.
 * 
 * This is the PRIMARY interface users interact with when they want to:
 * - Work through / edit the docs attached to a mission (+ quick notes)
 * - View and modify all mission metadata (status, priority, dates, tags, etc.)
 * - See deliverables, inputs, and attachments
 * 
 * LAYOUT STRUCTURE ("Mission Docs Become Real Docs"):
 * -----------------
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ HEADER: Breadcrumbs (Campaign > Nested Campaign > Mission) + Close     │
 * ├─────────────────────────────────────────────────┬───────────────────────┤
 * │ MAIN CONTENT AREA (AttachedDocsSection)         │ RIGHT SIDEBAR         │
 * │ - Icon + Title, plaintext quick notes            │ - Details (default) │
 * │ - Attached Doc records (open in place,           │ - Conditions        │
 * │   mission context: ticks save per mission)       │                     │
 * └─────────────────────────────────────────────────┴───────────────────────┘
 *
 * The mission no longer owns a block editor or User/Admin content tiers —
 * content lives in real Doc records (doc-level permission via the
 * `admin-only` tag). Legacy fields (notes/internalNotes/docUser/docAdmin)
 * are frozen backups; AttachedDocsSection surfaces a manual "Transfer to a
 * doc / Clear old notes" control for missions that still carry them.
 * 
 * CSS class prefix: .mission-full-view__ and .mfv-
 * ============================================================================
 */

// Sidebar tabs (Details = status, priority, dates, etc. — default)
const SIDEBAR_TABS = [
  { id: 'details', label: 'Details' },
  { id: 'conditions', label: 'Conditions' },
];

const TaskDetailSidebar = ({
  isOpen,
  onClose,
  task,
  // Data
  allTasks = [],
  statuses = [],
  allCategories = [],
  allTags = [],
  nestedSprints = [],
  allSprints = [],
  lockStates = {},
  // Session doc stack (owned by the tab)
  initialDocStack = [],
  missionDocDirect = false,
  onDocStackChange,
  // Tab restore: appear in the open/closed surface state with no enter/exit anim
  skipSurfaceAnimation = false,
  onSkipSurfaceAnimationConsumed,
  // Breadcrumb data
  breadcrumbPath = [],
  currentCampaign = null,
  // Callbacks
  onStatusChange,
  onPriorityChange,
  onDifficultyChange,
  onDueDateChange,
  onRemindersChange,
  onEstimatedDurationMinutesChange,
  onCreatedAtChange,
  onStartedAtChange,
  onCompletedAtChange,
  onUpdatedAtChange,
  onCategoryChange,
  onRenameTask,
  onIconChange,
  onAddStatus,
  onDeleteStatus,
  onRenameStatus,
  onSetStatusAutoSync,
  // Tag callbacks
  onAddTag,
  onRemoveTag,
  onSetPrimaryTag,
  onRenameTag,
  onDeleteTag,
  onGoToTask,
  onSelectSprint,
  onOpenNestedSprint,
  onRemoveNestedSprint,
  onDeleteTask,
  onMissionDocTicksChange,
  onMissionDocFilesChange,
  onMissionVariablesChange,
  onAttachedDocsChange,
  onQuickNotesChange,
  onMissionDocsMigrated,
  onClearMissionLegacyNotes,
  // Variable resolution context for attached docs (LIVE campaign object)
  variableCampaign,
  onCampaignVariablesChange,
  onPatchInheritedVariable,
  // Custom fields support
  customFields = [],
  onCustomFieldValueChange,
  // Conditions editing
  onConditionsChange,
  // Deliverables editing
  onEditDeliverables,
  // Deliverables submission
  onSubmitDeliverable,
  // Clear deliverable submission
  onClearDeliverable,
}) => {
  const { role } = useAuth();
  const canViewAdminDocs = (role ?? 'admin') !== 'user';
  const docSheetOpen = useDocSheetOpen();
  const overlayHost = useAppMainOverlayHost();

  const [activeTab, setActiveTab] = useState('details');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Autosave lives inside the sections now (quick notes / doc panes flush on
  // unmount), so closing is just closing
  const closeWithFlush = onClose;

  useEffect(() => {
    setActiveTab('details');
  }, [task?.id]);

  // Tab restore: keep the skip flag until the mission task is available.
  useLayoutEffect(() => {
    if (!skipSurfaceAnimation) return undefined;
    if (isOpen && !task) return undefined;
    onSkipSurfaceAnimationConsumed?.();
    return undefined;
  }, [skipSurfaceAnimation, isOpen, task, onSkipSurfaceAnimationConsumed]);
  
  // Keyboard handler for Escape
  useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (isVariablesPanelOpen()) return;
        if (isAttachmentPreviewOpen()) return;
        closeWithFlush();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeWithFlush]);
  
  if (!isOpen || !task) return null;

  const displayTask = task;

  // Stop wheel events from propagating
  const handleWheel = (e) => {
    e.stopPropagation();
  };

  // Lock state
  const lockState = lockStates[displayTask.id] || {};
  const hasNestedSprint = !!displayTask.nestedSprintId;
  const nestedSprint = nestedSprints.find(s => s.id === displayTask.nestedSprintId);

  // Build full breadcrumb including current campaign
  const fullBreadcrumb = [
    ...breadcrumbPath,
    ...(currentCampaign ? [currentCampaign] : []),
  ];

  // Render sidebar content based on active tab
  const renderSidebarContent = () => {
    if (activeTab === 'details') {
      return (
        <div className="mfv-sidebar__content">
          <MetadataBar
            variant="sidebar"
            task={displayTask}
            statuses={statuses}
            allTasks={allTasks}
            allCategories={allCategories}
            allTags={allTags}
            isLocked={lockState.isLocked}
            lockReason={lockState.lockReason}
            blockingDependencies={lockState.blockingTasks || []}
            hasNestedSprint={hasNestedSprint}
            nestedSprint={nestedSprint}
            nestedSprints={nestedSprints}
            allSprints={allSprints}
            onStatusChange={onStatusChange}
            onAddStatus={onAddStatus}
            onDeleteStatus={onDeleteStatus}
            onRenameStatus={onRenameStatus}
            onSetStatusAutoSync={onSetStatusAutoSync}
            onPriorityChange={onPriorityChange}
            onDifficultyChange={onDifficultyChange}
            onAddTag={onAddTag}
            onRemoveTag={onRemoveTag}
            onSetPrimaryTag={onSetPrimaryTag}
            onRenameTag={onRenameTag}
            onDeleteTag={onDeleteTag}
            onDueDateChange={onDueDateChange}
            onRemindersChange={onRemindersChange}
            onEstimatedDurationMinutesChange={onEstimatedDurationMinutesChange}
            onStartedAtChange={onStartedAtChange}
            onCompletedAtChange={onCompletedAtChange}
            onSubmitDeliverable={onSubmitDeliverable}
          />
        </div>
      );
    }

    return (
      <div className="mfv-sidebar__content">
        <MetadataSection 
          task={displayTask}
          allTasks={allTasks}
          statuses={statuses}
          nestedSprints={nestedSprints}
          hasNestedSprint={hasNestedSprint}
          nestedSprint={nestedSprint}
          onOpenNestedSprint={onOpenNestedSprint}
          onRemoveNestedSprint={onRemoveNestedSprint}
          onConditionsChange={onConditionsChange}
          onEditDeliverables={onEditDeliverables}
          onSubmitDeliverable={onSubmitDeliverable}
          onClearDeliverable={onClearDeliverable}
        />
        
        {customFields && customFields.length > 0 && (
          <CustomFieldsSidebarSection
            task={displayTask}
            customFields={customFields}
            onCustomFieldValueChange={onCustomFieldValueChange}
          />
        )}
      </div>
    );
  };

  // No remount key on mission change — MissionDocSheet must stay mounted
  // across tab switches so the doc surface doesn't disappear/reappear.
  // useMissionDocNavigation reseeds the stack when taskId changes.
  const attachedDocsSection = (
    <AttachedDocsSection
      task={displayTask}
      taskId={displayTask.id}
      taskName={displayTask.name}
      taskIcon={displayTask.icon}
      onRenameTask={onRenameTask}
      onIconChange={onIconChange}
      canViewAdminDocs={canViewAdminDocs}
      onAttachedDocsChange={onAttachedDocsChange}
      onQuickNotesChange={onQuickNotesChange}
      onMissionDocsMigrated={onMissionDocsMigrated}
      onClearMissionLegacyNotes={onClearMissionLegacyNotes}
      onMissionDocTicksChange={onMissionDocTicksChange}
      onMissionDocFilesChange={onMissionDocFilesChange}
      onMissionVariablesChange={onMissionVariablesChange}
      variableCampaign={variableCampaign}
      allSprints={allSprints}
      onCampaignVariablesChange={onCampaignVariablesChange}
      onPatchInheritedVariable={onPatchInheritedVariable}
      initialDocStack={initialDocStack}
      missionDocDirect={missionDocDirect}
      onDocStackChange={onDocStackChange}
    />
  );

  const content = (
    <div 
      className={`mission-full-view mission-full-view--open${docSheetOpen ? ' mission-full-view--doc-editing' : ''}`}
      onWheel={handleWheel}
    >
      {/* Keep the mission veil mounted while a doc sheet opens — tearing it
          down the same frame the doc backdrop starts fading in flashes the
          light canvas underneath (and ViewContainer's suspended placeholder). */}
      <div
        className="mission-full-view__backdrop"
        onClick={docSheetOpen ? undefined : closeWithFlush}
        aria-hidden={docSheetOpen || undefined}
      />
      
      {/* Modal panel — collapsed while editing a doc; AttachedDocsSection stays
          mounted so MissionDocSheet state (open doc, back stack) survives */}
      <div
        className={`mission-full-view__panel ${docSheetOpen ? 'mission-full-view__panel--doc-hidden' : ''}`}
      >
        <div className="mission-full-view__panel-inner">
          {!docSheetOpen && (
          <header className="mission-full-view__header">
            {/* Breadcrumb path */}
            <nav className="mission-full-view__breadcrumb">
              {fullBreadcrumb.map((item) => (
                <React.Fragment key={item.id}>
                  <button 
                    className="mission-full-view__breadcrumb-item"
                    onClick={() => onSelectSprint?.(item)}
                    title={item.name}
                  >
                    {item.icon && (
                      <span className="mission-full-view__breadcrumb-icon">
                        {renderIcon(item.icon, { size: '12px' })}
                      </span>
                    )}
                    <span className="mission-full-view__breadcrumb-name">{item.name}</span>
                  </button>
                  <span className="mission-full-view__breadcrumb-sep">/</span>
                </React.Fragment>
              ))}
              {/* Current mission name in breadcrumb */}
              <span className="mission-full-view__breadcrumb-current">
                {displayTask.name}
              </span>
            </nav>
            
            {/* Header actions */}
            <div className="mission-full-view__header-actions">
              <button 
                className="mission-full-view__delete-btn" 
                onClick={() => setShowDeleteConfirm(true)} 
                title="Move to trash"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
              <DocSheetCloseButton onClose={closeWithFlush} />
            </div>
          </header>
          )}

          <div className="mission-full-view__body">
            <main className="mission-full-view__main">
              {attachedDocsSection}
            </main>

            {!docSheetOpen && (
            <aside className="mission-full-view__sidebar">
              <div className="mfv-sidebar__tabs">
                {SIDEBAR_TABS.map(tab => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`mfv-sidebar__tab ${activeTab === tab.id ? 'mfv-sidebar__tab--active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              {renderSidebarContent()}
            </aside>
            )}
          </div>
        </div>
      </div>
      
      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Move to Trash"
        message={`Move "${displayTask.name}" to trash? You can restore it later from the Trash.`}
        confirmLabel="Move to Trash"
        cancelLabel="Cancel"
        confirmVariant="warning"
        icon="🗑"
        onConfirm={() => {
          setShowDeleteConfirm(false);
          onDeleteTask?.(displayTask.id);
          closeWithFlush();
        }}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );

  return ReactDOM.createPortal(content, overlayHost || document.body);
};

export default TaskDetailSidebar;
